import { Upload } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import {
  CompanyAddressDto,
  VehicleRecallDto,
  VehicleRecallRepairStatusDto,
  VehicleRecallStatusDto,
  vehicleRecallValidationSchema
} from 'dtos'
import { Form, Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { Junction } from 'localConstants'
import { useEffect, useState } from 'react'

interface RecallCampaignAddEditDialogProps {
  initialValues: VehicleRecallDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: VehicleRecallDto) => void
  open: boolean
}

export default function RecallCampaignAddEditDialog({
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: RecallCampaignAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingCompanyAddresses: false,
    isGettingVehicleRecallRepairStatuses: false,
    isGettingVehicleRecallStatuses: false
  })

  // const [isGettingCompanyAddresses, setIsGettingCompanyAddresses] = useState<boolean>(false)
  const [addresses, setAddresses] = useState<CompanyAddressDto[]>([])
  const [vehicleRecallStatuses, setVehicleRecallStatuses] = useState<VehicleRecallStatusDto[]>([])
  const [vehicleRecallRepairStatuses, setVehicleRecallRepairStatuses] = useState<VehicleRecallRepairStatusDto[]>([])

  useEffect(() => {
    setLoadingState('isGettingCompanyAddresses', true)
    controllers
      .getCompaniesAddresses({
        page: 0,
        pageSize: 10,
        statuses: ['isActive', 'isRecallLocationAddress'],
        junction: Junction.AND
      })
      .then(response => {
        setAddresses(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingCompanyAddresses', false)
      })

    setLoadingState('isGettingVehicleRecallRepairStatuses', true)
    controllers
      .getVehicleRecallRepairStatuses()
      .then(response => {
        setVehicleRecallRepairStatuses(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleRecallRepairStatuses', false)
      })

    setLoadingState('isGettingVehicleRecallStatuses', true)
    controllers
      .getVehicleRecallStatuses()
      .then(response => {
        setVehicleRecallStatuses(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleRecallStatuses', false)
      })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={vehicleRecallValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.vehicleRecallGuid ? 'Edit' : 'Add'} Recall</DialogTitle>
                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      options={vehicleRecallStatuses}
                      getOptionLabel={(option: VehicleRecallStatusDto) => `${option.name}`}
                      onChange={(_e, value) => {
                        setFieldValue('vehicleRecallStatus', value)

                        if (value?.specialIdentifier !== 'RECALL') {
                          setFieldValue('canadianOEMSiteScreenshotFile', null)
                          setFieldValue('canadianOEMSiteScreenshotFileName', null)
                          setFieldValue('usaOEMSiteScreenshotFile', null)
                          setFieldValue('usaOEMSiteScreenshotFileName', null)
                          setFieldValue('canadianOEMSiteURL', '')
                          setFieldValue('usaOEMSiteURL', '')
                        } else {
                          setFieldValue('canadianOEMSiteScreenshotFile', null)
                          setFieldValue('usaOEMSiteScreenshotFile', null)
                          setFieldValue('repairOrderFile', null)
                          setFieldValue('canadianOEMSiteURL', null)
                          setFieldValue('usaOEMSiteURL', null)
                        }
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='Recall / No Recall'
                          error={Boolean(touched.vehicleRecallStatus && errors.vehicleRecallStatus)}
                          helperText={touched.vehicleRecallStatus && errors.vehicleRecallStatus}
                          onBlur={handleBlur}
                        />
                      )}
                      value={values.vehicleRecallStatus ? values.vehicleRecallStatus : null}
                    />
                  </Grid>

                  {values.vehicleRecallStatus?.specialIdentifier === 'RECALL' && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.recallCampaignId && errors.recallCampaignId)}
                          fullWidth
                          helperText={touched.recallCampaignId && errors.recallCampaignId}
                          label='Recall Campaign ID'
                          name='recallCampaignId'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.recallCampaignId || ''}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.recallDescription && errors.recallDescription)}
                          helperText={touched.recallDescription && errors.recallDescription}
                          label='Recall Description'
                          maxRows={4}
                          minRows={4}
                          multiline
                          name='recallDescription'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.recallDescription}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.recallCountry && errors.recallCountry)}
                          helperText={touched.recallCountry && errors.recallCountry}
                          label='Recall Country'
                          name='recallCountry'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='string'
                          select
                          value={values.recallCountry}
                        >
                          <MenuItem value='USA'>USA</MenuItem>
                          <MenuItem value='Canada'>Canada</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.repairCountry && errors.repairCountry)}
                          helperText={touched.repairCountry && errors.repairCountry}
                          label='Repair Country'
                          name='repairCountry'
                          onBlur={handleBlur}
                          onChange={e => {
                            setFieldValue('repairCountry', e.target.value)

                            if (e.target.value === 'Canada') {
                              setFieldValue('repairLocationAddress', undefined)
                            }
                          }}
                          type='string'
                          select
                          value={values.repairCountry}
                        >
                          <MenuItem value='USA'>USA</MenuItem>
                          <MenuItem value='Canada'>Canada</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          autoHighlight
                          autoSelect
                          disabled={
                            isLoading ||
                            isSubmitting ||
                            loadingState.isGettingCompanyAddresses ||
                            values.repairCountry !== 'USA'
                          }
                          options={addresses}
                          getOptionLabel={(option: CompanyAddressDto) =>
                            `${option.company?.name ?? ''} - ${option.addressNickname ?? ''}`
                          }
                          onChange={(_e, value) => {
                            setFieldValue('repairLocationAddress', value)
                          }}
                          renderOption={(props, option) => (
                            <li {...props}>
                              {option.company?.name ?? ''} - {option.addressNickname ?? ''}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label='Repair Location'
                              error={Boolean(touched.repairLocationAddress && errors.repairLocationAddress)}
                              helperText={touched.repairLocationAddress && errors.repairLocationAddress}
                              onBlur={handleBlur}
                            />
                          )}
                          value={
                            values.repairLocationAddress
                              ? addresses.filter(
                                  address => address.addressGuid === values.repairLocationAddress?.addressGuid
                                )[0] ?? values.repairLocationAddress
                              : null
                          }
                        />
                      </Grid>

                      {values.repairLocationAddress && (
                        <Grid item xs={12} sx={{ ml: 1 }}>
                          <Typography variant='body1'>{values.repairLocationAddress.address1}</Typography>
                          {values.repairLocationAddress.address2 && (
                            <Typography variant='body1'>{values.repairLocationAddress.address2}</Typography>
                          )}
                          <Typography variant='body1'>
                            {values.repairLocationAddress.city}, {values.repairLocationAddress.stateProvince}{' '}
                            {values.repairLocationAddress.postalCode}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Autocomplete
                          autoHighlight
                          autoSelect
                          disabled={isLoading || isSubmitting}
                          options={vehicleRecallRepairStatuses}
                          getOptionLabel={(option: VehicleRecallRepairStatusDto) => `${option.name}`}
                          onChange={(_e, value) => {
                            setFieldValue('vehicleRecallRepairStatus', value)
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label='Repair Status'
                              error={Boolean(touched.vehicleRecallRepairStatus && errors.vehicleRecallRepairStatus)}
                              helperText={touched.vehicleRecallRepairStatus && errors.vehicleRecallRepairStatus}
                              onBlur={handleBlur}
                            />
                          )}
                          value={values.vehicleRecallRepairStatus ? values.vehicleRecallRepairStatus : null}
                        />
                      </Grid>

                      <Grid item container xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <LabelField
                            label='Canadian OEM Site Screenshot'
                            value={
                              values.canadianOEMSiteScreenshotFile?.name ??
                              values.canadianOEMSiteScreenshotFileName ??
                              'No File Uploaded'
                            }
                          />
                        </Grid>

                        <Grid item>
                          <input
                            accept='.png,.jpg,.jpeg,.pdf'
                            hidden
                            type='file'
                            id='canadianOEMSiteScreenshotInput'
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                setFieldValue('canadianOEMSiteScreenshotFile', e.target.files[0])
                              }
                            }}
                          />

                          <Button
                            disabled={isLoading || isSubmitting}
                            onClick={() => {
                              document.getElementById('canadianOEMSiteScreenshotInput')?.click()
                            }}
                            variant='contained'
                            endIcon={<Upload />}
                          >
                            UPLOAD
                          </Button>
                        </Grid>

                        {touched.canadianOEMSiteScreenshotFile && errors.canadianOEMSiteScreenshotFile && (
                          <Grid item xs={12}>
                            <FormHelperText error>{errors.canadianOEMSiteScreenshotFile}</FormHelperText>
                          </Grid>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.canadianOEMSiteURL && errors.canadianOEMSiteURL)}
                          fullWidth
                          helperText={touched.canadianOEMSiteURL && errors.canadianOEMSiteURL}
                          label='Canadian OEM Site URL'
                          name='canadianOEMSiteURL'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.canadianOEMSiteURL || ''}
                        />
                      </Grid>

                      <Grid item container xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <LabelField
                            label='USA OEM Site Screenshot'
                            value={
                              values.usaOEMSiteScreenshotFile?.name ?? values.usaOEMSiteScreenshotFileName ?? 'No File Uploaded'
                            }
                          />
                        </Grid>

                        <Grid item>
                          <input
                            accept='.png,.jpg,.jpeg,.pdf'
                            hidden
                            type='file'
                            id='usaOEMSiteScreenshotInput'
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                setFieldValue('usaOEMSiteScreenshotFile', e.target.files[0])
                              }
                            }}
                          />

                          <Button
                            disabled={isLoading || isSubmitting}
                            onClick={() => {
                              document.getElementById('usaOEMSiteScreenshotInput')?.click()
                            }}
                            variant='contained'
                            endIcon={<Upload />}
                          >
                            UPLOAD
                          </Button>
                        </Grid>

                        {touched.usaOEMSiteScreenshotFile && errors.usaOEMSiteScreenshotFile && (
                          <Grid item xs={12}>
                            <FormHelperText error>{errors.usaOEMSiteScreenshotFile}</FormHelperText>
                          </Grid>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          disabled={isLoading || isSubmitting}
                          error={Boolean(touched.usaOEMSiteURL && errors.usaOEMSiteURL)}
                          fullWidth
                          helperText={touched.usaOEMSiteURL && errors.usaOEMSiteURL}
                          label='USA OEM Site URL'
                          name='usaOEMSiteURL'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.usaOEMSiteURL || ''}
                        />
                      </Grid>

                      <Grid item container xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <LabelField
                            label='Repair Order (RO)'
                            value={values.repairOrderFile?.name ?? values.repairOrderFileName ?? 'No File Uploaded'}
                          />
                        </Grid>

                        <Grid item>
                          <input
                            accept='.png,.jpg,.jpeg,.pdf'
                            hidden
                            type='file'
                            id='repairOrderFileInput'
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                setFieldValue('repairOrderFile', e.target.files[0])
                              }
                            }}
                          />

                          <Button
                            disabled={isLoading || isSubmitting}
                            onClick={() => {
                              document.getElementById('repairOrderFileInput')?.click()
                            }}
                            variant='contained'
                            endIcon={<Upload />}
                          >
                            UPLOAD
                          </Button>
                        </Grid>

                        {errors.repairOrderFile && (
                          <Grid item xs={12}>
                            <FormHelperText error>{errors.repairOrderFile}</FormHelperText>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <GreenText variant='outlined'>
                            {`When “Recall” is selected, “Recall Campaign ID” becomes a required field.

Recall Country is the country that initiated the recall. Recall Country options are
Canada and USA.

Repair Country is the country where the recall will be repaired. Repair Country
options are Canada and USA.

Repair Location is enabled only if “Repair Country” = USA. If Repair Location is
enabled, it is a required field. The Repair Location dropdown shows the name of
any Companies that have at least one address with “Repair Location” toggled on.
If a company has more than one address with “Repair Location” toggled on, that
company name will be listed multiple times (once of each address with “Repair
Location” toggled on).

Repair Status options are “No Remedy”, “No Parts”, “Remedy”, and “Repaired”. “No
Remedy” means that there is no fix available for the recall. “No Parts” means that
a fix is available, but parts to fix the recall are not available. “Remedy” means that
a fix is available. “Repaired” means that the recall has been repaired.

If Repair Status = “Repaired”, Repair Order (RO) upload becomes required.`}
                          </GreenText>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {values.vehicleRecallStatus?.specialIdentifier === 'NO_RECALL' && (
                    <>
                      <Grid item container xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <LabelField
                            label='Canadian OEM Site Screenshot'
                            value={
                              values.canadianOEMSiteScreenshotFile?.name ??
                              values.canadianOEMSiteScreenshotFileName ??
                              'No File Uploaded'
                            }
                          />
                        </Grid>

                        <Grid item>
                          <input
                            accept='.png,.jpg,.jpeg,.pdf'
                            hidden
                            type='file'
                            id='canadianOEMSiteScreenshotInput'
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                setFieldValue('canadianOEMSiteScreenshotFile', e.target.files[0])
                              }
                            }}
                          />

                          <Button
                            disabled={isLoading || isSubmitting}
                            onClick={() => {
                              document.getElementById('canadianOEMSiteScreenshotInput')?.click()
                            }}
                            variant='contained'
                            endIcon={<Upload />}
                          >
                            UPLOAD
                          </Button>
                        </Grid>

                        {touched.canadianOEMSiteScreenshotFile && errors.canadianOEMSiteScreenshotFile && (
                          <Grid item xs={12}>
                            <FormHelperText error>{errors.canadianOEMSiteScreenshotFile}</FormHelperText>
                          </Grid>
                        )}
                      </Grid>

                      <Grid item container xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <LabelField
                            label='USA OEM Site Screenshot'
                            value={
                              values.usaOEMSiteScreenshotFile?.name ?? values.usaOEMSiteScreenshotFileName ?? 'No File Uploaded'
                            }
                          />
                        </Grid>

                        <Grid item>
                          <input
                            accept='.png,.jpg,.jpeg,.pdf'
                            hidden
                            type='file'
                            id='usaOEMSiteScreenshotInput'
                            onChange={e => {
                              if (e.target.files && e.target.files.length > 0) {
                                setFieldValue('usaOEMSiteScreenshotFile', e.target.files[0])
                              }
                            }}
                          />

                          <Button
                            disabled={isLoading || isSubmitting}
                            onClick={() => {
                              document.getElementById('usaOEMSiteScreenshotInput')?.click()
                            }}
                            variant='contained'
                            endIcon={<Upload />}
                          >
                            UPLOAD
                          </Button>
                        </Grid>

                        {touched.usaOEMSiteScreenshotFile && errors.usaOEMSiteScreenshotFile && (
                          <Grid item xs={12}>
                            <FormHelperText error>{errors.usaOEMSiteScreenshotFile}</FormHelperText>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <GreenText variant='outlined'>
                            {`If "No Recall" is selected, both "Canadian OEM Site Screenshot" and "USA OEM Site Screenshot" uploads are required. `}
                          </GreenText>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton
                  loading={isLoading || isSubmitting}
                  onClick={() => {
                    console.log(errors)
                  }}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
